import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import authService from '../../services/auth.service';
import branding from '../../config/branding';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  // Use orange color directly: rgb(245, 106, 44) = #F56A2C
  const primaryColor = '#F56A2C';
  
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberMe: false,
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.login({
        identifier: formData.identifier,
        password: formData.password,
      });

      if (formData.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Create lighter/darker variations of primary color
  const primaryRgba = hexToRgba(primaryColor, 1);
  const primaryLight = hexToRgba(primaryColor, 0.15);
  const primaryLighter = hexToRgba(primaryColor, 0.08);
  const primaryDark = hexToRgba(primaryColor, 0.25);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'dark' 
          ? `linear-gradient(135deg, #0a0e27 0%, ${hexToRgba(primaryColor, 0.2)} 30%, #1a1a2a 60%, ${hexToRgba(primaryColor, 0.15)} 100%)`
          : `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.1)} 0%, ${hexToRgba(primaryColor, 0.05)} 30%, #f5f7fa 60%, ${hexToRgba(primaryColor, 0.08)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '@keyframes lightLoop': {
          '0%': {
            opacity: 0.3,
            transform: 'translateX(-100%) translateY(-100%)',
          },
          '50%': {
            opacity: 0.6,
          },
          '100%': {
            opacity: 0.3,
            transform: 'translateX(100%) translateY(100%)',
          },
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(-30px, -30px) scale(1.1)' },
        },
        '@keyframes pulse': {
          '0%, 100%': { opacity: 0.4, transform: 'scale(1)' },
          '50%': { opacity: 0.8, transform: 'scale(1.05)' },
        },
      }}
    >
      {/* Animated light loop background - moving gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: `conic-gradient(from 0deg at 50% 50%, 
            transparent 0deg, 
            ${hexToRgba(primaryColor, 0.15)} 60deg, 
            transparent 120deg, 
            ${hexToRgba(primaryColor, 0.1)} 180deg, 
            transparent 240deg, 
            ${hexToRgba(primaryColor, 0.12)} 300deg, 
            transparent 360deg)`,
          animation: 'lightLoop 20s linear infinite',
          zIndex: 0,
          filter: 'blur(60px)',
        }}
      />
      
      {/* Pulsing center light orb */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0.1)} 40%, transparent 70%)`,
          filter: 'blur(100px)',
          zIndex: 0,
          animation: 'pulse 6s ease-in-out infinite',
          transform: 'translate(-50%, -50%)',
        }}
      />
      
      {/* Additional light sweep */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(135deg, 
            transparent 0%, 
            ${hexToRgba(primaryColor, 0.1)} 25%, 
            transparent 50%, 
            ${hexToRgba(primaryColor, 0.08)} 75%, 
            transparent 100%)`,
          animation: 'lightLoop 12s ease-in-out infinite',
          zIndex: 0,
          filter: 'blur(40px)',
        }}
      />

      {/* Decorative background elements with primary color */}
      <Box
        sx={{
          position: 'absolute',
          top: '-40%',
          right: '-30%',
          width: '700px',
          height: '700px',
          borderRadius: '50%',
          background: theme.palette.mode === 'dark'
            ? primaryDark
            : primaryLight,
          filter: 'blur(100px)',
          zIndex: 0,
          animation: 'float 20s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-25%',
          left: '-15%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: theme.palette.mode === 'dark'
            ? primaryLight
            : primaryLighter,
          filter: 'blur(90px)',
          zIndex: 0,
          animation: 'float 25s ease-in-out infinite reverse',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: theme.palette.mode === 'dark'
            ? hexToRgba(primaryColor, 0.12)
            : hexToRgba(primaryColor, 0.08),
          filter: 'blur(70px)',
          zIndex: 0,
          animation: 'float 18s ease-in-out infinite',
        }}
      />

      <Container 
        maxWidth="md"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          {/* Left: Brand / work image */}
          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', md: 'block' },
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              boxShadow: `0 18px 45px ${hexToRgba(primaryColor, 0.35)}`,
              transform: 'translateY(0)',
              transition: 'transform 0.6s ease, box-shadow 0.6s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(145deg, ${hexToRgba(primaryColor, 0.25)} 0%, transparent 35%, transparent 65%, ${hexToRgba(primaryColor, 0.3)} 100%)`,
                mixBlendMode: 'soft-light',
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 24px 60px ${hexToRgba(primaryColor, 0.45)}`,
              },
            }}
          >
            <Box
              component="img"
              src="/login.jpeg"
              alt="DARB roadside assistance"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>

          {/* Right: Logo + login form */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {/* Logo Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2.5,
                  p: 3,
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.2)}, rgba(255, 255, 255, 0.08))`
                    : `linear-gradient(135deg, rgba(255, 255, 255, 0.98), ${hexToRgba(primaryColor, 0.06)})`,
                  backdropFilter: 'blur(20px)',
                  border: `2px solid ${theme.palette.mode === 'dark' ? hexToRgba(primaryColor, 0.3) : hexToRgba(primaryColor, 0.22)}`,
                  boxShadow: `0 10px 36px ${hexToRgba(primaryColor, 0.2)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 16px 50px ${hexToRgba(primaryColor, 0.3)}`,
                  },
                }}
              >
                <img
                  src="/cropped-pixelcut-export-removebg-2048x691.png"
                  alt="DARB National Services Co LLC"
                  style={{
                    height: '90px',
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                  onLoad={() => console.log('Logo loaded successfully')}
                  onError={(e) => {
                    console.error('Logo failed to load:', e);
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.7)})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textAlign: 'center',
                  mb: 0.75,
                }}
              >
                {t('auth.login')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  textAlign: 'center',
                  fontWeight: 500,
                }}
              >
                Welcome back! Please sign in to continue.
              </Typography>
            </Box>

            {/* Login Form Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                width: '100%',
                maxWidth: '450px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                background: theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, rgba(20, 20, 30, 0.95), rgba(25, 25, 40, 0.95))`
                  : `linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))`,
                backdropFilter: 'blur(20px)',
                border: `2px solid ${theme.palette.mode === 'dark' ? hexToRgba(primaryColor, 0.2) : hexToRgba(primaryColor, 0.15)}`,
                boxShadow: `0 8px 32px ${hexToRgba(primaryColor, 0.2)}, 0 0 0 1px ${hexToRgba(primaryColor, 0.1)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.6)}, ${primaryColor})`,
                  zIndex: 1,
                },
              }}
            >
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2, 
                  width: '100%',
                  borderRadius: 1,
                }}
              >
                {error}
              </Alert>
            )}

            <Box 
              component="form" 
              onSubmit={handleSubmit} 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                width: '100%',
              }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="identifier"
                label={t('auth.email')}
                name="identifier"
                autoComplete="email"
                autoFocus
                value={formData.identifier}
                onChange={handleChange}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: primaryColor,
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('auth.password')}
                type="password"
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor,
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: primaryColor,
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      color="primary"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  }
                  label={t('auth.rememberMe')}
                  sx={{ userSelect: 'none' }}
                />
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 2,
                  mb: 1,
                  py: 1.75,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.8)})`,
                  boxShadow: `0 4px 16px ${hexToRgba(primaryColor, 0.3)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent, ${hexToRgba(primaryColor, 0.3)}, transparent)`,
                    transition: 'left 0.5s ease',
                  },
                  '&:hover': {
                    background: `linear-gradient(135deg, ${hexToRgba(primaryColor, 0.9)}, ${primaryColor})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${hexToRgba(primaryColor, 0.4)}`,
                    '&::before': {
                      left: '100%',
                    },
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('auth.login')
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
      </Container>
    </Box>
  );
};

export default Login;
